import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import useGameBase from "../../hooks/useGameBase";
import SpectatorPerspectiveSelector from "./SpectatorPerspectiveSelector";
// WhirdleHash: 4 intersecting 5-letter words in a hash (#) shape
// 2 horizontal, 2 vertical, intersect at 2nd and 4th letters
// Player swaps letters to solve; green = correct position

// Board mixing is now handled by the server

function getFeedback(board, solution) {
  // Returns { green: ["w-l"], yellow: ["w-l"] }
  const green = [];
  const yellow = [];

  // Helper: count letters in array
  function counter(wordArr) {
    const count = {};
    for (const char of wordArr) {
      count[char] = (count[char] || 0) + 1;
    }
    return count;
  }

  // Get board words as arrays
  const boardWords = board.map(row => row.map(c => c || ''));
  const solutionWords = solution.map(row => row.map(c => c || ''));

  // Compute feedback for each word
  function computeWordFeedback(wordIdx) {
    const guessArr = boardWords[wordIdx];
    const targetArr = solutionWords[wordIdx];
    const targetCount = counter(targetArr);
    const feedback = Array(5).fill('B');
    // First pass: greens
    for (let i = 0; i < 5; i++) {
      if (guessArr[i] === targetArr[i] && guessArr[i] !== '') {
        feedback[i] = 'G';
        targetCount[guessArr[i]]--;
      }
    }
    // Second pass: yellows
    for (let i = 0; i < 5; i++) {
      if (feedback[i] === 'B' && targetCount[guessArr[i]] > 0 && guessArr[i] !== '') {
        feedback[i] = 'Y';
        targetCount[guessArr[i]]--;
      }
    }
    return feedback;
  }

  // For each word, get feedback
  const wordFeedbacks = [0,1,2,3].map(computeWordFeedback);

  // For each cell, resolve color based on all words it belongs to
  for (let wordIdx = 0; wordIdx < 4; wordIdx++) {
    for (let letterIdx = 0; letterIdx < 5; letterIdx++) {
      // Find all words this cell belongs to
      const cellKeys = [];
      // Horizontal words: wordIdx 0 and 1
      if (wordIdx === 0 || wordIdx === 1) cellKeys.push([wordIdx, letterIdx]);
      // Vertical words: wordIdx 2 and 3
      if (wordIdx === 2 || wordIdx === 3) cellKeys.push([wordIdx, letterIdx]);
      // For intersections, add both
      // Actually, for hash shape, intersections are at specific positions
      // We'll resolve by checking all wordFeedbacks for this cell
      let cellColor = 'B';
      for (const [w, l] of cellKeys) {
        const color = wordFeedbacks[w][l];
        if (color === 'G') cellColor = 'G';
        else if (color === 'Y' && cellColor !== 'G') cellColor = 'Y';
      }
      if (cellColor === 'G') green.push(`${wordIdx}-${letterIdx}`);
      else if (cellColor === 'Y') yellow.push(`${wordIdx}-${letterIdx}`);
    }
  }
  return { green, yellow };
}

function WhirdleHashGame({ gameState, gamePlayers = [], user = {}, socket, roomId, isSpectator = false }) {
  // Track expanded player for large view
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  // Main grid: interactive, shows letters
  function renderMainHashGrid(board, greens, yellows, selected, dragState, hoverCellKey, handleCellClick, handleCellPointerDown, readOnly = false, compact = false) {
    return (
      <div className="grid mt-4 grid-cols-5 gap-2 mb-10" style={{ transform: 'scale(1)', marginTop: compact ? '-60px' : '0px' }}>
        {[0,1,2,3,4].map(rowIdx =>
          [0,1,2,3,4].map(colIdx => {
            let w = null, l = null, letter = "";
            if (rowIdx === 1) { w = 0; l = colIdx; letter = board[0][colIdx]; }
            if (rowIdx === 3) { w = 1; l = colIdx; letter = board[1][colIdx]; }
            if (colIdx === 1) { w = 2; l = rowIdx; letter = board[2][rowIdx]; }
            if (colIdx === 3) { w = 3; l = rowIdx; letter = board[3][rowIdx]; }
            if (rowIdx === 1 && colIdx === 1) { w = 2; l = 1; letter = board[2][1]; }
            if (rowIdx === 1 && colIdx === 3) { w = 3; l = 1; letter = board[3][1]; }
            if (rowIdx === 3 && colIdx === 1) { w = 2; l = 3; letter = board[2][3]; }
            if (rowIdx === 3 && colIdx === 3) { w = 3; l = 3; letter = board[3][3]; }
            const isHashCell = (rowIdx === 1 || rowIdx === 3 || colIdx === 1 || colIdx === 3);
            if (!isHashCell) return <div key={`empty-${rowIdx}-${colIdx}`}></div>;
            const cellKey = `${w}-${l}`;
            // For intersection cells, check both word/letter pairs
            let isGreen = false, isYellow = false;
            if (w !== null && l !== null) {
              // Find intersection pair for this cell
              const intersectionPair = [cellKey];
              // Add the other intersecting cell if applicable
              if ((rowIdx === 1 && colIdx === 1)) intersectionPair.push(`0-1`);
              if ((rowIdx === 1 && colIdx === 3)) intersectionPair.push(`0-3`);
              if ((rowIdx === 3 && colIdx === 1)) intersectionPair.push(`1-1`);
              if ((rowIdx === 3 && colIdx === 3)) intersectionPair.push(`1-3`);
              isGreen = intersectionPair.some(key => greens.includes(key));
              isYellow = intersectionPair.some(key => yellows.includes(key));
            }
            let cellClass = "bg-gray-200 dark:bg-gray-700 text-black dark:text-gray-100";
            if (isGreen) cellClass = "bg-green-500";
            else if (isYellow) cellClass = "bg-yellow-400";
            // For other players, just show a label, no button, no selection
            if (compact) {
              return (
                <div
                  key={`${rowIdx}-${colIdx}`}
                  className={`w-12 h-12 rounded border flex items-center justify-center transition-all ${cellClass} border-gray-400 dark:border-gray-600 shadow font-bold text-lg`}
                  style={{ pointerEvents: 'none' }}
                >
                  {letter}
                </div>
              );
            }
            // ...normal button for self...
            const isSelected = w !== null && l !== null && selected.some(([sw, sl]) => sw === w && sl === l);
            const isDragSource = !!dragState && dragState.source[0] === w && dragState.source[1] === l && dragState.isDragging;
            const isDragHover = hoverCellKey === cellKey && !isDragSource;
            if (isSelected) cellClass = "bg-blue-400";
            if (isDragHover) cellClass += " ring-2 ring-blue-300 dark:ring-blue-500 scale-[1.03]";
            if (isDragSource) cellClass += " opacity-30";
            return (
              <button
                key={`${rowIdx}-${colIdx}`}
                data-hash-cell={cellKey}
                onClick={() => w !== null && l !== null && handleCellClick(w, l)}
                onPointerDown={(event) => {
                  if (w === null || l === null || readOnly || isGreen) return;
                  handleCellPointerDown(event, w, l, letter);
                }}
                className={`w-12 h-12 rounded border flex items-center justify-center transition-all ${cellClass} border-gray-400 dark:border-gray-600 shadow font-bold text-lg`}
                style={{ touchAction: readOnly ? 'auto' : 'none' }}
                disabled={readOnly || isGreen || w === null || l === null}
              >
                {letter}
              </button>
            );
          })
        )}
      </div>
    );
  }

  // Mini grid: not interactive, no letters
  function renderMiniHashGrid(board, greens, yellows) {
    return (
      <div className="grid grid-cols-5 gap-1 mb-0" style={{ transform: 'scale(0.4)', opacity: 0.7, marginTop: '-60px' }}>
        {[0,1,2,3,4].map(rowIdx =>
          [0,1,2,3,4].map(colIdx => {
            let w = null, l = null;
            if (rowIdx === 1) { w = 0; l = colIdx; }
            if (rowIdx === 3) { w = 1; l = colIdx; }
            if (colIdx === 1) { w = 2; l = rowIdx; }
            if (colIdx === 3) { w = 3; l = rowIdx; }
            if (rowIdx === 1 && colIdx === 1) { w = 2; l = 1; }
            if (rowIdx === 1 && colIdx === 3) { w = 3; l = 1; }
            if (rowIdx === 3 && colIdx === 1) { w = 2; l = 3; }
            if (rowIdx === 3 && colIdx === 3) { w = 3; l = 3; }
            const isHashCell = (rowIdx === 1 || rowIdx === 3 || colIdx === 1 || colIdx === 3);
            if (!isHashCell) return <div key={`empty-${rowIdx}-${colIdx}`}></div>;
            const isGreen = w !== null && l !== null && greens.includes(`${w}-${l}`);
            const isYellow = w !== null && l !== null && yellows.includes(`${w}-${l}`);
            let cellClass = "bg-gray-200 dark:bg-gray-700";
            if (isGreen) cellClass = "bg-green-500";
            else if (isYellow) cellClass = "bg-yellow-400";
            return (
              <div
                key={`${rowIdx}-${colIdx}`}
                className={`w-12 h-12 rounded border flex items-center justify-center transition-all ${cellClass} border-gray-400 dark:border-gray-600 shadow`}
                style={{ pointerEvents: 'none' }}
              />
            );
          })
        )}
      </div>
    );
  }

  // Animation child component inside main component
  function ExpandedMiniHash({ expanded, pBoard, pFeedback, isOtherPlayer }) {
    const [animExpanded, setAnimExpanded] = useState(false);
    React.useEffect(() => {
      if (expanded) {
        setAnimExpanded(false);
        const t = setTimeout(() => setAnimExpanded(true), 10);
        return () => clearTimeout(t);
      } else {
        setAnimExpanded(false);
      }
    }, [expanded]);
    return expanded ? (
      <div
        className="mt-2 whirdle-expand-anim"
        style={{
          transition: 'transform 320ms cubic-bezier(.2,.9,.2,1), opacity 320ms cubic-bezier(.2,.9,.2,1)',
          transform: animExpanded ? 'scale(1)' : 'scale(0.8)',
          opacity: animExpanded ? 1 : 0.5,
        }}
      >
        {renderMainHashGrid(pBoard, pFeedback.green, pFeedback.yellow, [], null, null, () => {}, () => {}, true, isOtherPlayer)}
      </div>
    ) : null;
  }

  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.playersData) && gameState.playersData.length > 0) return gameState.playersData;
    return [];
  }, [gamePlayers, gameState?.playersData]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!Array.isArray(perspectiveOptions) || perspectiveOptions.length === 0) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  const { submitAction, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer });

  const matchesPerspectivePlayer = useMemo(() => {
    const idSet = new Set((perspectiveIds || []).filter(Boolean).map((value) => String(value)));
    return (player) => {
      if (!player || idSet.size === 0) return false;
      return [player.socketId, player.userId, player.id, player.username]
        .filter((value) => value !== undefined && value !== null)
        .some((value) => idSet.has(String(value)));
    };
  }, [perspectiveIds]);

  const players = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.playersData) && gameState.playersData.length > 0) return gameState.playersData;
    return [];
  }, [gamePlayers, gameState?.playersData]);

  const viewedPlayer = useMemo(() => {
    return players.find(matchesPerspectivePlayer) || null;
  }, [players, matchesPerspectivePlayer]);

  const viewedPlayerSocketId = useMemo(() => {
    if (viewedPlayer?.socketId) return viewedPlayer.socketId;
    if (socket?.id && !isSpectator) return socket.id;
    const playerStateKey = (perspectiveIds || []).find((id) => gameState?.playerStates?.[id]);
    return playerStateKey || null;
  }, [viewedPlayer?.socketId, socket?.id, isSpectator, perspectiveIds, gameState?.playerStates]);

  const words = useMemo(() => {
    if (Array.isArray(gameState?.initialBoard) && gameState.initialBoard.length > 0) return gameState.initialBoard;
    if (Array.isArray(gameState?.words) && gameState.words.length > 0) return gameState.words.map((w) => Array.isArray(w) ? w : String(w).split(""));
    return ['ABOUT', 'WOULD', 'EBOOK', 'BUILD'].map((w) => w.split(""));
  }, [gameState?.initialBoard, gameState?.words]);
  const solution = useMemo(() => (gameState?.solution ? gameState.solution : words), [gameState?.solution, words]);
  const board = useMemo(() => {
    if (gameState?.playerStates && viewedPlayerSocketId && gameState.playerStates[viewedPlayerSocketId]?.board) {
      return gameState.playerStates[viewedPlayerSocketId].board;
    }
    return words;
  }, [gameState?.playerStates, viewedPlayerSocketId, words]);
  const [selected, setSelected] = useState([]); // [w, l] pairs
  const [dragState, setDragState] = useState(null);
  const [hoverCellKey, setHoverCellKey] = useState(null);
  const suppressClickRef = useRef(false);
  const feedback = getFeedback(board, solution);
  const greens = feedback.green;
  const yellows = feedback.yellow;
  const otherPlayers = useMemo(() => {
    return players.filter((player) => !matchesPerspectivePlayer(player));
  }, [players, matchesPerspectivePlayer]);
  const showSpectatorPerspectiveSelector = isSpectator && perspectiveOptions.length > 0;

  useEffect(() => {
    setSelected([]);
  }, [viewedPlayerSocketId]);

  const handleSwapPositions = useCallback((from, to) => {
    if (!submitAction) return;
    submitAction({ type: "swap", positions: [from, to] });
  }, [submitAction]);

  useEffect(() => {
    if (!dragState) return undefined;

    const resolveHoverCellKey = (clientX, clientY) => {
      if (typeof document === 'undefined') return null;
      const target = document.elementFromPoint(clientX, clientY);
      const cell = target?.closest?.('[data-hash-cell]');
      const cellKey = cell?.getAttribute?.('data-hash-cell') || null;
      if (!cellKey) return null;
      if (greens.includes(cellKey)) return null;
      return cellKey;
    };

    const handlePointerMove = (event) => {
      if (event.pointerId !== dragState.pointerId) return;
      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;
      const hasStartedDragging = dragState.isDragging || Math.hypot(deltaX, deltaY) > 8;
      const nextHoverCellKey = hasStartedDragging ? resolveHoverCellKey(event.clientX, event.clientY) : null;

      setDragState((previous) => {
        if (!previous || previous.pointerId !== event.pointerId) return previous;
        return {
          ...previous,
          currentX: event.clientX,
          currentY: event.clientY,
          isDragging: hasStartedDragging,
        };
      });

      setHoverCellKey(nextHoverCellKey);
      if (hasStartedDragging) {
        setSelected([dragState.source]);
      }
    };

    const handlePointerUp = (event) => {
      if (event.pointerId !== dragState.pointerId) return;

      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;
      const hasDragged = dragState.isDragging || Math.hypot(deltaX, deltaY) > 8;
      const nextHoverCellKey = hasDragged ? resolveHoverCellKey(event.clientX, event.clientY) : null;

      if (hasDragged && nextHoverCellKey) {
        const [sourceWordIndex, sourceLetterIndex] = dragState.source;
        const [targetWordIndex, targetLetterIndex] = nextHoverCellKey.split('-').map(Number);
        if (sourceWordIndex !== targetWordIndex || sourceLetterIndex !== targetLetterIndex) {
          handleSwapPositions([sourceWordIndex, sourceLetterIndex], [targetWordIndex, targetLetterIndex]);
        }
        suppressClickRef.current = true;
      }

      setDragState(null);
      setHoverCellKey(null);
      if (hasDragged) {
        setSelected([]);
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [dragState, greens, handleSwapPositions]);

  function handleCellClick(w, l) {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    if (isSpectator) return;
    if (gameState.finished) return; // Disable moves if game is over
    
    if (greens.includes(`${w}-${l}`)) return; // Can't select green
    if (selected.length === 0) {
      setSelected([[w, l]]);
    } else if (selected.length === 1) {
      const [w1, l1] = selected[0];
      handleSwapPositions([w1, l1], [w, l]);
      setSelected([]);
    }
  }

  function handleCellPointerDown(event, w, l, letter) {
    if (isSpectator || gameState.finished) return;
    if (greens.includes(`${w}-${l}`)) return;
    suppressClickRef.current = false;
    setHoverCellKey(null);
    setDragState({
      source: [w, l],
      letter,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      currentX: event.clientX,
      currentY: event.clientY,
      isDragging: false,
    });
  }

  // Hash shape: horizontal words at rows 1 and 3, vertical at cols 1 and 3
  // Intersections: (0,1)-(2,1) and (0,3)-(2,3), (1,1)-(3,1) and (1,3)-(3,3)
  // Show results if game is finished
  const showResults = gameState.finished && gameState.result && gameState.result.results;
  // Find a consistent game start time for all players
  let gameStartTime = gameState.startedAt;
  if (!gameStartTime && gameState.result && gameState.result.results) {
    const allTimes = Object.values(gameState.result.results).map(r => r.finishedAt).filter(Boolean);
    if (allTimes.length > 0) {
      gameStartTime = Math.min(...allTimes) - 1;
    }
  }
  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-900 p-4">
      <style>{`
        .whirdle-expand-anim {
          will-change: transform, opacity;
        }
      `}</style>
      {showSpectatorPerspectiveSelector && (
        <div className="w-full max-w-sm mb-4">
          <SpectatorPerspectiveSelector
            players={perspectiveOptions}
            value={spectatorViewPlayerId || ""}
            onChange={setSpectatorViewPlayerId}
          />
        </div>
      )}
      {/* Main player board (interactive) */}
      {renderMainHashGrid(board, greens, yellows, selected, dragState, hoverCellKey, handleCellClick, handleCellPointerDown, isSpectator, false)}
      {dragState?.isDragging && (
        <div
          className="pointer-events-none fixed z-50 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded border border-gray-400 bg-blue-400/95 text-lg font-bold text-black shadow-2xl dark:border-gray-500 dark:bg-blue-500/95 dark:text-white"
          style={{ left: dragState.currentX, top: dragState.currentY }}
        >
          {dragState.letter}
        </div>
      )}
      {/* Results table when finished */}
      {showResults && (
        <div className="mt-6 w-full max-w-xl">
          <h2 className="text-lg font-bold mb-2 text-center dark:text-white">Results</h2>
          <table className="w-full border border-gray-400 dark:border-gray-600 rounded">
            <thead>
              <tr className="bg-gray-200 dark:bg-gray-700 dark:text-white">
                <th className="p-2">Player</th>
                <th className="p-2">Moves</th>
                <th className="p-2">Time (s)</th>
              </tr>
            </thead>
            <tbody>
              {players.map(p => {
                const res = gameState.result.results[p.socketId];
                if (!res) return null;
                let timeTaken = '';
                if (res.elapsed) {
                  timeTaken = res.elapsed;
                } else {
                  timeTaken = 'Did Not Finish';
                }
                return (
                  <tr key={p.socketId} className="border-t border-gray-300 dark:border-gray-700 dark:text-white">
                    <td className="p-2 text-center">{p.username || p.socketId}</td>
                    <td className="p-2 text-center">{res.moves}</td>
                    <td className="p-2 text-center">{timeTaken}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* Mini boards for other players (non-interactive, only clickable at game end) */}
      <div className="flex flex-row flex-wrap gap-2 mt-4 justify-center items-end">
        {otherPlayers.map(p => {
          const pState = gameState.playerStates[p.socketId];
          if (!pState) return null;
          const pBoard = pState.board;
          const pFeedback = getFeedback(pBoard, solution);
          const isExpanded = expandedPlayer === p.socketId;
          return (
            <div
              key={p.socketId}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
              <div
                onClick={gameState.finished ? () => setExpandedPlayer(isExpanded ? null : p.socketId) : undefined}
                style={gameState.finished ? { cursor: 'pointer' } : { cursor: 'default' }}
              >
                <div className="text-xs text-center mb-1 text-gray-700 dark:text-gray-300">{p.username || p.socketId}</div>
                {renderMiniHashGrid(pBoard, pFeedback.green, pFeedback.yellow)}
              </div>
              <ExpandedMiniHash expanded={isExpanded} pBoard={pBoard} pFeedback={pFeedback} isOtherPlayer={true} />
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default WhirdleHashGame;

WhirdleHashGame.Guide = function WhirdleHashGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">Whirdle Hash — Guide</h3>
      <ul className="list-disc ml-6">
        <li>Swap letters to solve the hash!</li>
        <li>4 words form a hash (#) shape, intersecting at 2nd and 4th letters.</li>
        <li>Find all green positions.</li>
        <li>Either drag and drop to swap letters or click on a letter and then a second letter to swap them.</li>
        <li>A yellow letter indicates a correct letter in the wrong position.</li>
      </ul>
    </div>
  );
};

      